import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

function normalizeToIsoDateOnly(value) {
    if (!value) return "";

    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value.trim())) {
        return value.trim();
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}

function isoToDate(value) {
    const iso = normalizeToIsoDateOnly(value);
    if (!iso) return undefined;
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? undefined : d;
}

function formatDateIt(value) {
    const iso = normalizeToIsoDateOnly(value);
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    return `${d}/${m}/${y}`;
}

export default function InlineDatePicker({
    value,
    onChange,
    placeholder = "gg/mm/aaaa",
    minDate,
}) {
    const [open, setOpen] = useState(false);
    const fieldRef = useRef(null);
    const popRef = useRef(null);
    const [pos, setPos] = useState({ top: 0, left: 0 });

    const selectedDay = useMemo(() => isoToDate(value), [value]);
    const minDay = useMemo(() => isoToDate(minDate), [minDate]);

    useLayoutEffect(() => {
        if (!open || !fieldRef.current) return;

        const rect = fieldRef.current.getBoundingClientRect();
        const popupWidth = 270;
        const popupHeight = 320;
        const margin = 6;

        let left = rect.left;
        let top = rect.bottom + margin;

        if (left + popupWidth > window.innerWidth - 8) {
            left = window.innerWidth - popupWidth - 8;
        }
        if (left < 8) left = 8;

        if (top + popupHeight > window.innerHeight - 8) {
            top = Math.max(8, rect.top - popupHeight - margin);
        }

        setPos({ top, left });
    }, [open]);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!open) return;
            if (
                popRef.current &&
                !popRef.current.contains(e.target) &&
                fieldRef.current &&
                !fieldRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };

        const onKey = (e) => {
            if (e.key === "Escape") setOpen(false);
        };

        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);

        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    const handleSelect = (day) => {
        if (!day) {
            onChange?.("");
            return;
        }

        const yyyy = day.getFullYear();
        const mm = String(day.getMonth() + 1).padStart(2, "0");
        const dd = String(day.getDate()).padStart(2, "0");
        onChange?.(`${yyyy}-${mm}-${dd}`);
        setOpen(false);
    };

    return (
        <div style={{ position: "relative" }}>
            <button
                ref={fieldRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                style={{
                    width: "100%",
                    minHeight: 44,
                    padding: "10px 12px",
                    border: "1px solid #d7dce1",
                    borderRadius: 12,
                    background: "#fff",
                    textAlign: "left",
                    cursor: "pointer",
                    fontSize: 14,
                    color: value ? "#1f2937" : "#6b7280",
                }}
            >
                {value ? formatDateIt(value) : placeholder}
            </button>

            {open && (
                <div
                    ref={popRef}
                    style={{
                        position: "fixed",
                        top: pos.top,
                        left: pos.left,
                        zIndex: 99999,
                        width: 270,
                        background: "#fff",
                        border: "1px solid #d7dce1",
                        borderRadius: 12,
                        boxShadow: "0 10px 24px rgba(0,0,0,.14)",
                        padding: 8,
                    }}
                >
                    <DayPicker
                        mode="single"
                        selected={selectedDay}
                        onSelect={handleSelect}
                        weekStartsOn={1}
                        showOutsideDays
                        disabled={minDay ? { before: minDay } : undefined}
                        styles={{
                            root: {
                                fontSize: 12,
                            },
                            month: {
                                margin: 0,
                            },
                            caption: {
                                marginBottom: 6,
                            },
                            caption_label: {
                                fontSize: 13,
                                fontWeight: 700,
                                textTransform: "capitalize",
                            },
                            head_cell: {
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#6b7280",
                            },
                            day: {
                                width: 30,
                                height: 30,
                                borderRadius: 8,
                                fontSize: 12,
                                margin: 0,
                            },
                            day_selected: {
                                backgroundColor: "#2563eb",
                                color: "#fff",
                            },
                            day_today: {
                                border: "1px dashed #2563eb",
                            },
                        }}
                    />

                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            style={{
                                border: "1px solid #d7dce1",
                                background: "#fff",
                                borderRadius: 10,
                                padding: "6px 10px",
                                cursor: "pointer",
                                fontSize: 12,
                            }}
                        >
                            Chiudi
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}